import { pb, escapeFilterValue } from '../lib/pocketbase'
import type { Client, AppUser } from '../types'
import { getAgencyId } from '../types/user'
import { logger } from '../utils/logger'
import { handleApiError } from '../utils/apiErrorHandler'
import { ensureUserAgency } from '../utils/ensureAgency'
import { fetchHeadshotAsync } from '../utils/headshotService'
import { initializeSyncContract } from '../utils/clientSyncContract'
import { cascadeDelete, fieldFilter, containsFilter } from '../utils/cascadeDeleteHelper'
import { guardedUpdate } from '../utils/optimisticLock'
import { buildPromotion, type PromotionInput } from '../shared/promotion'
import { listTasks, updateTask } from './tasks'
import { listNotes, updateNote } from './notes'
import { applyStandingFromClient } from '../utils/standingWrite'

export const KNOWN_CLIENT_FIELDS = new Set([
  'first_name', 'last_name', 'full_name',
  'headshot', 'headshot_url',
  // Each social link has its own column. Until Oct 2026 the first two were
  // missing here, so an Instagram or X link typed into the form was dropped.
  'instagram_url', 'twitter_url', 'tik_tok_url', 'youtube_url',
  'client_type', 'status', 'roster_status', 'priority',
  'sport', 'league', 'team', 'team_name', 'position', 'jersey_number',
  'date_of_birth', 'height', 'weight',
  'contract_value', 'contract_apy', 'annual_value', 'signing_bonus',
  'guaranteed_money', 'contract_years', 'cap_hit', 'current_cap_hit', 'contract_end_date',
  'draft_year', 'draft_round', 'draft_pick', 'projected_round', 'draft_status',
  'performance_stats', 'stats_summary', 'career_highlights', 'awards', 'injury_history',
  'email', 'phone', 'address',
  'social_media', 'social_links', 'media_links', 'external_ids', 'external_links',
  'data_snapshots', 'last_contact_at', 'profile_sync',
  'agent_owner_id', 'owner_id', 'agency_id', 'current_team_id', 'converted_from_prospect_id', 'lead_agent_id',
  'lifetime_earnings', 'this_year_earnings', 'commission_earned',
  'tags', 'notes', 'updated_by',
])

export type ClientPayload = {
  // Name fields - at least one of full_name OR (first_name + last_name) is required
  first_name?: string
  last_name?: string
  full_name?: string // Can be provided directly or computed from first/last
  nickname?: string | null // e.g. "Sauce" for Ahmad Gardner
  // Type & Status - defaults applied if not provided
  client_type?: 'athlete' | 'coach'
  status?: string | null
  roster_status?: string | null // Team standing (Active Roster, IR, Two-Way, Redshirt, …)
  // Media
  headshot?: File | null // For file upload
  headshot_url?: string | null
  instagram_url?: string | null
  twitter_url?: string | null
  tik_tok_url?: string | null
  youtube_url?: string | null
  // Sport Info
  sport?: string | null
  league?: string | null
  team?: string | null
  team_name?: string | null
  position?: string | null
  jersey_number?: string | null
  // Personal Info
  date_of_birth?: string | null
  height?: string | null
  weight?: string | null
  // Priority
  priority?: Client['priority']
  // Contract Info
  contract_value?: number | null
  contract_apy?: number | null
  annual_value?: number | null
  signing_bonus?: number | null
  guaranteed_money?: number | null
  contract_years?: number | null
  cap_hit?: number | null
  current_cap_hit?: number | null
  contract_end_date?: string | null
  // Draft Info
  draft_year?: number | null
  draft_round?: number | null
  draft_pick?: number | null
  projected_round?: string | null
  draft_status?: string | null
  // Performance & Stats
  performance_stats?: Record<string, any> | null
  stats_summary?: Record<string, any> | null
  career_highlights?: string | null
  awards?: string | null
  injury_history?: string | null
  // Contact Info
  email?: string | null
  phone?: string | null
  address?: string | null
  // Social & Links
  social_media?: Record<string, string> | null
  social_links?: Record<string, any> | null
  media_links?: Record<string, string> | null
  external_ids?: Record<string, any> | null
  external_links?: Record<string, any> | null
  profile_sync?: import('../shared/profileSync').ProfileSync | null
  // Tracking
  data_snapshots?: Record<string, any> | null
  last_contact_at?: string | null
  // Relations
  current_team_id?: string | null
  // Financial tracking
  lifetime_earnings?: number | null
  this_year_earnings?: number | null
  commission_earned?: number | null
  // Tags & Notes
  tags?: string[] | null
  notes?: string | null
  // Agency
  agency_id?: string
  // Conversion tracking
  converted_from_prospect_id?: string | null
}


export type PaginatedClientsResult = {
  items: Client[]
  page: number
  perPage: number
  totalItems: number
  totalPages: number
}

// Helper function to parse JSON fields from PocketBase (stored as text)
function parseJSONField(value: any): any {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'object') return value // Already parsed
  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch {
      return value // Return as-is if not valid JSON
    }
  }
  return value
}

// Helper function to serialize JSON fields for PocketBase (store as text)
function serializeJSONField(value: any): string | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') {
    // Already a string, validate it's JSON
    try {
      JSON.parse(value)
      return value
    } catch {
      // Not valid JSON, wrap it
      return JSON.stringify(value)
    }
  }
  if (typeof value === 'object' || Array.isArray(value)) {
    return JSON.stringify(value)
  }
  return String(value)
}

function mapClient(record: Record<string, any>): Client {
  // Compute full_name if not present
  const firstName = record.first_name ?? ''
  const lastName = record.last_name ?? ''
  const fullName = record.full_name || `${firstName} ${lastName}`.trim()

  return {
    id: record.id,
    client_id: record.id, // Alias for backwards compatibility
    // Name fields
    first_name: firstName,
    last_name: lastName,
    full_name: fullName,
    nickname: record.nickname ?? null,
    // Media
    headshot: record.headshot ?? null,
    headshot_url: record.headshot_url ?? null,
    instagram_url: record.instagram_url || null,
    twitter_url: record.twitter_url || null,
    tik_tok_url: record.tik_tok_url || null,
    youtube_url: record.youtube_url || null,
    // Type & Status
    client_type: record.client_type ?? 'athlete',
    status: record.status ?? null,
    roster_status: record.roster_status ?? null,
    cross_checks: record.cross_checks ?? null,
    priority: record.priority ?? null,
    // Sport Info
    sport: record.sport ?? null,
    league: record.league ?? null,
    team: record.team ?? null,
    team_name: record.team_name ?? null,
    position: record.position ?? null,
    jersey_number: record.jersey_number ?? null,
    // Personal Info
    date_of_birth: record.date_of_birth ?? null,
    height: record.height ?? null,
    weight: record.weight ?? null,
    // Contract Info
    contract_value: record.contract_value ?? null,
    contract_apy: record.contract_apy ?? null,
    annual_value: record.annual_value ?? null,
    signing_bonus: record.signing_bonus ?? null,
    guaranteed_money: record.guaranteed_money ?? null,
    contract_years: record.contract_years ?? null,
    cap_hit: record.cap_hit ?? null,
    current_cap_hit: record.current_cap_hit ?? null,
    contract_end_date: record.contract_end_date ?? null,
    // Draft Info
    draft_year: record.draft_year ?? null,
    draft_round: record.draft_round ?? null,
    draft_pick: record.draft_pick ?? null,
    projected_round: record.projected_round ?? null,
    draft_status: record.draft_status ?? null,
    // Performance & Stats
    performance_stats: parseJSONField(record.performance_stats),
    stats_summary: parseJSONField(record.stats_summary),
    snap_counts: parseJSONField(record.snap_counts),
    career_highlights: record.career_highlights ?? null,
    awards: record.awards ?? null,
    injury_history: record.injury_history ?? null,
    // Contact Info
    email: record.email ?? null,
    phone: record.phone ?? null,
    address: record.address ?? null,
    // Social & Links
    social_media: parseJSONField(record.social_media),
    social_links: parseJSONField(record.social_links),
    media_links: parseJSONField(record.media_links),
    external_ids: parseJSONField(record.external_ids),
    external_links: parseJSONField(record.external_links),
    profile_sync: record.profile_sync && typeof record.profile_sync === 'object' ? record.profile_sync : null,
    // Snapshots & Tracking
    data_snapshots: parseJSONField(record.data_snapshots),
    last_contact_at: record.last_contact_at ?? null,
    // Relations
    agent_owner_id: record.agent_owner_id ?? null,
    assigned_agent_id: record.assigned_agent_id ?? null,
    owner_id: record.owner_id ?? null,
    agency_id: record.agency_id ?? '',
    current_team_id: record.current_team_id ?? null,
    converted_from_prospect_id: record.converted_from_prospect_id ?? null,
    // Financial tracking
    lifetime_earnings: record.lifetime_earnings ?? null,
    this_year_earnings: record.this_year_earnings ?? null,
    commission_earned: record.commission_earned ?? null,
    // Tags & Notes
    tags: (() => {
      const parsed = parseJSONField(record.tags)
      return Array.isArray(parsed) ? parsed : (parsed ? [parsed] : null)
    })(),
    notes: record.notes ?? null,
    // Timestamps
    created: record.created ?? '',
    updated: record.updated ?? '',
    // Audit trail
    updated_by: record.updated_by ?? null,
    // Relationship health
    health_score: record.health_score != null ? Number(record.health_score) : null,
    health_score_updated_at: record.health_score_updated_at ?? null,
  }
}

export async function listClients(options?: { limit?: number; filter?: string }) {
  try {
    // Get current user's agency for filtering
    const user = pb.authStore.model as AppUser | null
    const agencyId = getAgencyId(user)
    const isSuperAdmin = (user as any)?.role === 'superadmin'

    const userId = user?.id

    logger.debug('listClients check:', {
      userId,
      agencyId,
      isSuperAdmin,
    })

    // All users (including superadmins) see clients in their agency OR where they're the assigned agent.
    // Cross-agency visibility is handled by useWorkView passing an explicit filter via options.filter.
    if (!agencyId && !userId) {
      logger.debug('listClients: No agency and no user id - returning empty')
      return []
    }

    const conditions: string[] = []
    if (agencyId) {
      conditions.push(`agency_id = "${escapeFilterValue(agencyId)}"`)
    }
    if (userId) {
      conditions.push(`agent_owner_id = "${escapeFilterValue(userId)}"`)
    }
    const baseFilter = conditions.length > 0 ? `(${conditions.join(' || ')})` : ''

    // Combine with any additional filters (e.g. work view filter)
    const filter = options?.filter
      ? (baseFilter ? `(${baseFilter}) && (${options.filter})` : options.filter)
      : baseFilter
    logger.debug(`listClients: Using filter="${filter}"`)


    // Use pagination if limit is specified, otherwise fetch all
    let data: Record<string, any>[]
    if (options?.limit) {
      const result = await pb.collection('clients').getList<Record<string, any>>(1, options.limit, {
        ...(filter && { filter }),
        sort: 'full_name'
      })
      data = result.items
    } else {
      // Fetch with agency filter for data isolation (or no filter for super admin)
      data = await pb.collection('clients').getFullList<Record<string, any>>({
        ...(filter && { filter }),
        perPage: 500,
        skipTotal: true
      })

      // Sort client-side by full_name (only for non-limited results)
      data = data.sort((a, b) => {
        const nameA = (a.full_name || '').toLowerCase()
        const nameB = (b.full_name || '').toLowerCase()
        if (nameA < nameB) return -1
        if (nameA > nameB) return 1
        return 0
      })
    }
    return data.map(mapClient)
  } catch (err: any) {
    // Ignore autocancellation errors (status 0) - these are expected when components unmount
    const status = err?.status ?? err?.response?.status
    if (status === 0) {
      // Request was cancelled (component unmounted) - return empty array
      return []
    }
    const apiError = handleApiError(err, 'List clients')
    const errorToThrow = new Error(apiError.message)
    ;(errorToThrow as any).status = apiError.status
    throw errorToThrow
  }
}



export async function listClientsPaginated(
  page: number = 1,
  perPage: number = 25,
  options?: { sort?: string; filter?: string }
): Promise<PaginatedClientsResult> {
  
  // Get current user's agency for filtering
  const user = pb.authStore.model as AppUser | null
  const agencyId = getAgencyId(user)
  const userId = user?.id
  // All users (including superadmins) filter by their agency.
  // Cross-agency visibility is handled by useWorkView passing an explicit filter.
  if (!agencyId && !userId) {
    return { items: [], page: 1, perPage, totalItems: 0, totalPages: 0 }
  }

  const conditions: string[] = []
  if (agencyId) {
    conditions.push(`agency_id = "${escapeFilterValue(agencyId)}"`)
  }
  if (userId) {
    conditions.push(`agent_owner_id = "${escapeFilterValue(userId)}"`)
  }
  const agencyFilter = conditions.length > 0 ? `(${conditions.join(' || ')})` : ''
  const combinedFilter = options?.filter
    ? (agencyFilter ? `(${agencyFilter}) && (${options.filter})` : options.filter)
    : agencyFilter

  const queryOptions: { filter?: string; sort?: string } = {}
  if (combinedFilter) {
    queryOptions.filter = combinedFilter
  }
  // Only add sort if it's defined
  if (options?.sort) {
    queryOptions.sort = options.sort
  }

  const result = await pb.collection('clients').getList<Record<string, any>>(page, perPage, queryOptions)
  return {
    items: result.items.map(mapClient),
    page: result.page,
    perPage: result.perPage,
    totalItems: result.totalItems,
    totalPages: result.totalPages,
  }
}

export async function getClient(clientId: string) {
  const record = await pb.collection('clients').getOne<Record<string, any>>(clientId)
  return mapClient(record)
}

export async function createClient(
  payload: ClientPayload,
  ownerId: string,
  options?: { targetAgencyId?: string }
) {
  // Get user to check for agency_id
  const _user = pb.authStore.model

  // Clean payload - ONLY include fields that exist in confirmed schema
  const cleanedPayload: any = {}
  Object.keys(payload).forEach(key => {
    if (!KNOWN_CLIENT_FIELDS.has(key)) {
      logger.debug(`Skipping field "${key}" - not in schema`)
      return
    }
    
    const value = (payload as any)[key]
    // Only include if not undefined (null is OK) AND field exists in confirmed schema
    if (value !== undefined && value !== null && value !== '') {
      cleanedPayload[key] = value
    }
  })
  
  // Map owner_id to agent_owner_id if owner_id doesn't exist in schema
  // (schema.json shows agent_owner_id exists, but migrations should add owner_id)
  const createPayload: any = { 
    ...cleanedPayload
  }
  
  // Set both owner_id and agent_owner_id for compatibility
  // Fallback to current authenticated user if ownerId not provided
  const effectiveOwnerId = ownerId || pb.authStore.model?.id || ''
  if (effectiveOwnerId) {
    createPayload.owner_id = effectiveOwnerId
    createPayload.agent_owner_id = effectiveOwnerId
  }
  
  // Ensure client_type is set (required field)
  if (!createPayload.client_type) {
    createPayload.client_type = 'athlete' // Default to athlete
  }

  // Ensure status is set (required field)
  if (!createPayload.status) {
    createPayload.status = 'signed' // Default to signed
  }
  
  // Serialize JSON fields for PocketBase
  const jsonFields = [
    'performance_stats', 'stats_summary', 'social_media', 'social_links',
    'media_links', 'external_ids', 'external_links', 'data_snapshots', 'tags'
  ]
  jsonFields.forEach(field => {
    if (createPayload[field] !== undefined) {
      createPayload[field] = serializeJSONField(createPayload[field])
    }
  })
  
  // Assign agency_id: use explicit target if provided (superadmin backfill),
  // otherwise auto-detect/create from the logged-in user's agency.
  if (options?.targetAgencyId) {
    createPayload.agency_id = options.targetAgencyId
  } else {
    const userAgencyId = await ensureUserAgency()
    createPayload.agency_id = userAgencyId
  }
  
  // Final cleanup: Remove any fields not in schema AND remove undefined/null/empty values
  const finalPayload: any = {}
  Object.keys(createPayload).forEach(key => {
    // Only include if field exists in schema
    if (!KNOWN_CLIENT_FIELDS.has(key)) {
      logger.warn(`Removing field "${key}" - not in schema`)
      return
    }
    
    const value = createPayload[key]
    // Skip undefined/null/empty strings (but keep 0, false, etc.)
    if (value === undefined || value === null || value === '') {
      return
    }
    
    finalPayload[key] = value
  })
  
  // Handle name fields - PocketBase requires first_name and last_name
  // If only full_name is provided, split it into first/last
  if (!finalPayload.first_name || !finalPayload.last_name) {
    if (finalPayload.full_name) {
      const nameParts = finalPayload.full_name.trim().split(/\s+/)
      if (!finalPayload.first_name) {
        finalPayload.first_name = nameParts[0] ?? null
      }
      if (!finalPayload.last_name) {
        finalPayload.last_name = nameParts.slice(1).join(' ') || (nameParts[0] ?? null)
      }
    } else {
      throw new Error('Either full_name or first_name and last_name are required')
    }
  }

  // Compute full_name if not provided
  if (!finalPayload.full_name) {
    finalPayload.full_name = `${finalPayload.first_name} ${finalPayload.last_name}`.trim()
  }

  // Infer sport when omitted but league clearly indicates football.
  if (!finalPayload.sport && typeof finalPayload.league === 'string') {
    const leagueLower = finalPayload.league.toLowerCase()
    if (leagueLower.includes('nfl') || leagueLower.includes('college') || leagueLower.includes('ncaa') || leagueLower.includes('cfb')) {
      finalPayload.sport = 'Football'
    }
  }
  if (!finalPayload.league && typeof finalPayload.sport === 'string') {
    const sportLower = finalPayload.sport.toLowerCase()
    if (sportLower.includes('football')) {
      finalPayload.league = 'NFL'
    } else if (sportLower.includes('basketball')) {
      finalPayload.league = 'NBA'
    } else if (sportLower.includes('baseball')) {
      finalPayload.league = 'MLB'
    }
  }

  // Ensure client_type and status have defaults (required in PocketBase)
  if (!finalPayload.client_type) {
    finalPayload.client_type = 'athlete'
  }
  if (!finalPayload.status) {
    finalPayload.status = 'signed'
  }
  
  // Ensure agent_owner_id is set (required for access rules)
  // Only add owner_id if it exists in schema (added by migration)
  if (effectiveOwnerId) {
    finalPayload.agent_owner_id = effectiveOwnerId
    // Only add owner_id if it's in the known schema fields
    if (KNOWN_CLIENT_FIELDS.has('owner_id')) {
      finalPayload.owner_id = effectiveOwnerId
    }
  }

  // Deterministic onboarding + sync queue contract for every new client.
  const onboarding = {
    candidate_match: Boolean(payload.external_ids?.onboarding?.candidate_match),
    source_confirmed: Boolean(payload.external_ids?.onboarding?.source_confirmed),
    selected_source: payload.external_ids?.onboarding?.selected_source || null,
    selected_candidate_name: payload.external_ids?.onboarding?.selected_candidate_name || null,
    selected_candidate_sources: payload.external_ids?.onboarding?.selected_candidate_sources || [],
    created_at: new Date().toISOString(),
  }
  finalPayload.external_ids = initializeSyncContract(
    typeof finalPayload.external_ids === 'object' ? finalPayload.external_ids : null,
    onboarding
  )
  finalPayload.external_ids = serializeJSONField(finalPayload.external_ids)

  // Check authentication first
  const authUser = pb.authStore.model
  if (!authUser) {
    throw new Error('Not authenticated. Please log in again.')
  }

  try {
    const record = await pb.collection('clients').create(finalPayload)
    const client = mapClient(record)

    // Auto-fetch headshot for NFL/CFB players (async, doesn't block return)
    if (!client.headshot_url) {
      fetchHeadshotAsync(
        client.id,
        'clients',
        client.full_name,
        client.sport,
        client.league,
        async (id, data) => pb.collection('clients').update(id, data)
      )
    }

    return client
  } catch (err: any) {
    // Use error handler for consistent error handling
    const apiError = handleApiError(err, 'Client creation')

    // Re-throw with user-friendly message
    const errorToThrow = new Error(apiError.message)
    ;(errorToThrow as any).status = apiError.status
    ;(errorToThrow as any).fieldErrors = apiError.fieldErrors
    throw errorToThrow
  }
}

export async function updateClient(
  clientId: string,
  payload: Partial<ClientPayload>,
  lastUpdated?: string,
) {
  try {
    // Fetch previous state for automation triggers
    let previousClient: Client | null = null
    try {
      previousClient = await getClient(clientId)
    } catch {
      // Non-critical — automation triggers are best-effort
    }

    // Serialize JSON fields before sending to PocketBase
    const updatePayload: any = { ...payload }

    // Set updated_by for audit trail
    const user = pb.authStore.model
    if (user?.id) {
      updatePayload.updated_by = user.id
    }

    const jsonFields = [
      'performance_stats', 'stats_summary', 'social_media', 'social_links',
      'media_links', 'external_ids', 'external_links', 'data_snapshots', 'tags'
    ]
    jsonFields.forEach(field => {
      if (updatePayload[field] !== undefined) {
        updatePayload[field] = serializeJSONField(updatePayload[field])
      }
    })

    // Strip fields not in the PocketBase schema — same guard as createClient
    const filteredPayload: any = {}
    Object.entries(updatePayload).forEach(([key, value]) => {
      if (KNOWN_CLIENT_FIELDS.has(key) && value !== undefined) {
        filteredPayload[key] = value
      }
    })

    const record = await guardedUpdate('clients', clientId, filteredPayload, lastUpdated)
    const updatedClient = mapClient(record)

    // A roster status set by hand moves the pay strip and writes the
    // transaction row through the same rule the sync uses. The label is
    // already saved; if this part fails the error surfaces and Data Health
    // names the strip that did not follow.
    if ('roster_status' in payload && previousClient && String(previousClient.roster_status ?? '') !== String(updatedClient.roster_status ?? '')) {
      await applyStandingFromClient(updatedClient, previousClient.roster_status, updatedClient.roster_status, {
        date: (payload as Record<string, unknown>).roster_status_date as string | null | undefined,
      })
    }

    return updatedClient
  } catch (err: any) {
    const apiError = handleApiError(err, 'Update client')
    const errorToThrow = new Error(apiError.message)
    ;(errorToThrow as any).status = apiError.status
    ;(errorToThrow as any).fieldErrors = apiError.fieldErrors
    throw errorToThrow
  }
}

export async function deleteClient(clientId: string) {
  return deleteClientCascade(clientId)
}

/**
 * Delete client and all related records (cascade delete).
 * Fetches all related collections in parallel, deletes in batched parallel.
 * WARNING: This permanently deletes all data associated with the client.
 */
export async function deleteClientCascade(clientId: string) {
  try {
    logger.info('Starting cascade delete for client:', clientId)

    // The client goes first. If the caller is not allowed to delete it, the
    // rules refuse here and nothing else has been touched; the old order
    // destroyed the deals, tasks and notes first and then failed on the
    // client, leaving a half-emptied record and an error.
    await pb.collection('clients').delete(clientId)

    const eid = escapeFilterValue(clientId)
    await cascadeDelete([
      { collection: 'pvs_scores',        filter: fieldFilter('client_id', clientId) },
      { collection: 'contracts',          filter: fieldFilter('client_id', clientId) },
      { collection: 'deals',              filter: fieldFilter('client_id', clientId) },
      { collection: 'tasks',              filter: `client_id = "${eid}" || (related_entity_type = "client" && related_entity_id = "${eid}") || (entity_type = "client" && entity_id = "${eid}")` },
      { collection: 'notes',              filter: `client_id = "${eid}" || (related_entity_type = "client" && related_entity_id = "${eid}")` },
      { collection: 'compliance_items',   filter: fieldFilter('client_id', clientId) },
      { collection: 'invoices',           filter: fieldFilter('client_id', clientId) },
      { collection: 'transactions',       filter: fieldFilter('client_id', clientId) },
      { collection: 'commission_ledger',  filter: fieldFilter('client_id', clientId) },
      { collection: 'activities',         filter: fieldFilter('client_id', clientId) },
      { collection: 'calendar_events',    filter: fieldFilter('client_id', clientId) },
    ])

    // Synced games shared with other clients on the same club name this
    // client in client_ids with client_id empty. Take them off those rows;
    // a row left with nobody on it goes too.
    const shared = await pb.collection('calendar_events').getFullList<Record<string, any>>({
      filter: `${containsFilter('client_ids', clientId)} && client_id != "${eid}"`,
      fields: 'id,client_ids',
    })
    for (const row of shared) {
      const rest = (row.client_ids as string[]).filter(id => id !== clientId)
      if (rest.length === 0) await pb.collection('calendar_events').delete(row.id)
      else await pb.collection('calendar_events').update(row.id, { client_ids: rest, client_id: rest.length === 1 ? rest[0] : '' })
    }
    logger.info(`Client deleted successfully; removed from ${shared.length} shared calendar row(s)`)

  } catch (err: any) {
    const apiError = handleApiError(err, 'Cascade delete client')
    const errorToThrow = new Error(apiError.message)
    ;(errorToThrow as any).status = apiError.status
    throw errorToThrow
  }
}

/**
 * Find a client by name (fuzzy match on full_name).
 * Used to bridge player-name-based lookups with client_id-based data (e.g. NIL valuations).
 */
export async function findClientByName(playerName: string): Promise<Client | null> {
  const user = pb.authStore.model as AppUser | null
  const agencyId = getAgencyId(user)

  const filterParts: string[] = [
    `full_name ~ "${escapeFilterValue(playerName)}"`,
  ]
  if (agencyId) {
    filterParts.push(`agency_id = "${escapeFilterValue(agencyId)}"`)
  }

  const records = await pb.collection('clients').getList<Record<string, any>>(1, 1, {
    filter: filterParts.join(' && '),
    sort: 'full_name',
  })

  if (records.items.length === 0) return null
  return mapClient(records.items[0])
}

/**
 * Update data snapshots for a client
 */
export async function updateClientDataSnapshots(
  clientId: string,
  snapshots: Record<string, any>
) {
  // Merge, not replace. This wrote the column wholesale, so every enrichment
  // run discarded any key the caller did not happen to pass — including the
  // `sources` provenance the MCP records alongside researched values.
  const current = await pb.collection('clients').getOne(clientId)
  const existing = parseJSONField(current.data_snapshots) ?? {}
  const record = await pb.collection('clients').update(clientId, {
    data_snapshots: serializeJSONField({ ...existing, ...snapshots }),
  })
  return mapClient(record)
}

// ── Promotion ───────────────────────────────────────────────────────────

/**
 * A college or NIL client turns pro. One write: league, club, status,
 * roster status, ESPN id, headshot, and a note in external_ids of where
 * they came from. Guarded on the record's `updated` stamp like every other
 * edit. The schedule and roster jobs pick the new league up on their next
 * run; nothing here talks to ESPN.
 */
export async function promoteClient(
  clientId: string,
  input: PromotionInput,
  lastUpdated?: string,
): Promise<Client> {
  const current = await getClient(clientId)
  const payload = buildPromotion(current, { ...input, confirmed_by: pb.authStore.model?.id ?? null })
  return updateClient(clientId, payload as Partial<ClientPayload>, lastUpdated)
}

export interface AdoptedHistory {
  notes: number
  tasks: number
}

/**
 * Move a prospect's notes and tasks onto the client they became. The
 * prospect record itself stays (it is the recruiting history); what moves is
 * the working record, so the client page shows the calls and to-dos from
 * before signing. Throws on the first failure so a half-moved history is
 * reported rather than silently split.
 */
export async function adoptProspectHistory(prospectId: string, clientId: string): Promise<AdoptedHistory> {
  const notes = await listNotes({ related_entity_type: 'prospect', related_entity_id: prospectId })
  for (const note of notes) {
    await updateNote(note.id, { related_entity_type: 'client', related_entity_id: clientId })
  }
  // Tasks point at a prospect two ways: the prospect_id relation, or the
  // generic entity fields. Both are moved.
  const tasks = (await listTasks()).filter(t =>
    t.prospect_id === prospectId || (t.entity_type === 'prospect' && t.entity_id === prospectId),
  )
  for (const task of tasks) {
    // prospect_id is a relation on the collection the TaskPayload type omits.
    await updateTask(task.id, { client_id: clientId, prospect_id: null, entity_type: 'client', entity_id: clientId } as Parameters<typeof updateTask>[1] & { prospect_id: null })
  }
  return { notes: notes.length, tasks: tasks.length }
}
